import {
  HttpError,
  NetworkError,
  TimeoutError,
  UnexpectedHtmlError,
  responseErrorFor,
} from './errors';

type Params = Record<string, string | number | boolean | null | undefined>;

interface HttpBaseInit extends Omit<RequestInit, 'body'> {
  params?: Params;
  // 省略時は same origin
  baseUrl?: string;
  timeoutMs?: number;
}

// json と body を両方渡すと body が黙って捨てられるため、型で同時指定を禁止する
export type HttpInit = HttpBaseInit &
  (
    | {
        // body に JSON 文字列、Content-Type に application/json を設定する
        json?: unknown;
        body?: never;
      }
    | { json?: never; body?: BodyInit | null }
  );

// アプリ固有の振る舞い（認証ヘッダーやキーの変換など）を外から差し込むためのオプション
export interface HttpClientOptions {
  // 送信先の URL を受け取り、既定で付けるヘッダーを返す。呼び出し側の headers が優先する
  headers?: (url: URL) => HeadersInit;
  // 2xx の JSON を data として返す前に変換する。エラーの body には使わない
  transformData?: (data: unknown) => unknown;
}

interface Exchange {
  // エラーメッセージ用の "<METHOD> <path>"
  label: string;
  timeoutMs: number;
  signal: AbortSignal | null | undefined;
  // リクエストの開始から timeoutMs 経つと reject する。各段階はこれと race する
  timeout: Promise<never>;
  timedOut: boolean;
}

// request の戻り値。axios の response と同じく、transformData を通した body とヘッダーを持つ。
// Response そのものは渡さない。response.json() などで timeout・中断・transformData を迂回できてしまうため
export interface HttpResponse<T> {
  data: T;
  headers: Headers;
}

const DEFAULT_TIMEOUT_MS = 60000;
// スキーム付き（https://…）かプロトコル相対（//…）の URL
const ABSOLUTE_URL = /^([a-z][a-z\d+\-.]*:)?\/\//i;

export function createHttpClient(options: HttpClientOptions = {}) {
  const send = async <T>(method: string, path: string, init?: HttpInit) =>
    (await request<T>(options, path, { ...init, method })).data;

  return {
    // ok でなければ ResponseError 系を throw する。ページネーションのようにヘッダーも読みたい呼び出し側が使う
    request: <T>(path: string, init?: HttpInit) =>
      request<T>(options, path, init),
    get: <T>(path: string, init?: HttpInit) => send<T>('GET', path, init),
    post: <T>(path: string, init?: HttpInit) => send<T>('POST', path, init),
    put: <T>(path: string, init?: HttpInit) => send<T>('PUT', path, init),
    patch: <T>(path: string, init?: HttpInit) => send<T>('PATCH', path, init),
    delete: <T>(path: string, init?: HttpInit) => send<T>('DELETE', path, init),
  };
}

async function request<T>(
  options: HttpClientOptions,
  path: string,
  init: HttpInit = {},
): Promise<HttpResponse<T>> {
  const {
    params,
    json,
    baseUrl = '',
    timeoutMs = DEFAULT_TIMEOUT_MS,
    headers,
    signal,
    ...rest
  } = init;
  const method = (rest.method ?? 'GET').toUpperCase();
  // エラーメッセージはエラー監視などの外部に送られうるため、利用者の入力を含みうるクエリとフラグメントを除く
  const messagePath = path.replace(/[?#].*$/, '');
  // ブラウザの fetch と同じく、相対パスは <base href> も反映した document.baseURI を基準に解決する
  const url = new URL(buildUrl(baseUrl, path, params), document.baseURI);
  const isJson = json !== undefined;
  // AbortSignal.any は未対応ブラウザが残るため、内部の controller に呼び出し側の中断を転送する。
  // 転送は body の読み取りまで続けるので、ヘッダー受信後の abort でも body を中断できる
  const controller = new AbortController();
  // JSON.stringify の失敗や GET への body 指定など、Request を組み立てられない不正なリクエストはここで例外になる。
  // try の外で組み立てて、NetworkError にせずそのまま投げる。
  // Request の signal は controller.signal に追従する別オブジェクトになる
  const fetchRequest = new Request(url, {
    ...rest,
    method,
    headers: buildHeaders(options.headers?.(url), headers, isJson),
    body: isJson ? JSON.stringify(json) : rest.body,
    signal: controller.signal,
  });
  const forwardAbort = () => controller.abort(signal?.reason);
  if (signal?.aborted === true) {
    forwardAbort();
  } else {
    signal?.addEventListener('abort', forwardAbort, { once: true });
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  const exchange: Exchange = {
    label: `${method} ${messagePath}`,
    timeoutMs,
    signal,
    // axios と同じく、リクエストの開始から body を読み終えるまでを通して timeoutMs で打ち切る
    timeout: new Promise<never>((_resolve, reject) => {
      timer = setTimeout(() => {
        exchange.timedOut = true;
        controller.abort();
        reject(new Error('timeout'));
      }, timeoutMs);
    }),
    timedOut: false,
  };
  // race する段階がないうちに期限が来ても、unhandled rejection にならないようにする
  exchange.timeout.catch(() => undefined);
  try {
    const response = await runPhase(
      exchange,
      () => fetch(fetchRequest),
      `${exchange.label} failed`,
    );
    if (!response.ok) {
      throw responseErrorFor(
        method,
        messagePath,
        response.status,
        await readErrorBody(response, exchange),
      );
    }
    return {
      data: await parseJson<T>(response, exchange, options.transformData),
      headers: response.headers,
    };
  } finally {
    clearTimeout(timer);
    // 長寿命の signal にクロージャを溜めないよう、body を読み終えたら転送を外す
    signal?.removeEventListener('abort', forwardAbort);
  }
}

async function parseJson<T>(
  response: Response,
  exchange: Exchange,
  transformData: (data: unknown) => unknown = data => data,
): Promise<T> {
  const text = await readText(response, exchange);
  if (text === '') return undefined as T;

  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch (error) {
    // JSON の代わりに HTML が返った（ログイン画面へのリダイレクトなど）ことを区別できるよう、専用のエラーにする
    if (response.headers.get('Content-Type')?.includes('text/html') === true) {
      throw new UnexpectedHtmlError(`${exchange.label} returned HTML`, {
        cause: error,
      });
    }
    throw new HttpError(`${exchange.label} returned a non-JSON body`, {
      cause: error,
    });
  }
  return transformData(data) as T;
}

// axios の buildFullPath と同じく、path が絶対 URL なら baseUrl を付けず、そうでなければ境界のスラッシュを 1 つにまとめる
function buildUrl(baseUrl: string, path: string, params?: Params): string {
  const url =
    baseUrl === '' || ABSOLUTE_URL.test(path)
      ? path
      : `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  const search = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value != null) search.append(key, String(value));
  });
  const query = search.toString();
  if (query === '') return url;
  // フラグメントより後ろのクエリはサーバーに届かないため、フラグメントの前に付ける
  const hashIndex = url.indexOf('#');
  const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex);
  return `${base}${base.includes('?') ? '&' : '?'}${query}${hash}`;
}

// 既定のヘッダー、json の Content-Type、呼び出し側の headers の順に重ね、後のものを優先する
function buildHeaders(
  defaults: HeadersInit | undefined,
  init: HeadersInit | undefined,
  isJson: boolean,
): Headers {
  const headers = new Headers(defaults);
  if (isJson) headers.set('Content-Type', 'application/json');
  new Headers(init).forEach((value, key) => headers.set(key, value));
  return headers;
}

// body の途中で接続が切れた場合も HttpError 系で返す
async function readText(
  response: Response,
  exchange: Exchange,
): Promise<string> {
  return runPhase(
    exchange,
    () => response.text(),
    `${exchange.label} body could not be read`,
  );
}

// エラー body は JSON なら parse し、そうでなければ text、空か読めなければ null
async function readErrorBody(
  response: Response,
  exchange: Exchange,
): Promise<unknown> {
  let text: string;
  try {
    text = await readText(response, exchange);
  } catch {
    // 読み取り失敗を分類した後に abort されることもあるため、元のエラーではなく呼び出し側の中断として投げる
    if (exchange.signal?.aborted === true) throw callerAbortError(exchange);
    // ステータスは受け取れているので、NetworkError や TimeoutError に埋もれて 5xx を見落とさないよう ResponseError として返す
    return null;
  }
  if (text === '') return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

// fetch（ヘッダー受信まで）と body の読み取りを、リクエスト全体の期限（exchange.timeout）と race させて実行する。
// body が abort を無視しても、期限が来れば抜けられる
async function runPhase<T>(
  exchange: Exchange,
  run: () => Promise<T>,
  failureMessage: string,
): Promise<T> {
  try {
    const operation = run();
    // race に負けた側が unhandled rejection にならないようにする
    operation.catch(() => undefined);
    return await Promise.race([operation, exchange.timeout]);
  } catch (error) {
    // Chromium は呼び出し側の中断を TypeError: Failed to fetch で返すため、エラーの形ではなく signal で判定する
    if (exchange.signal?.aborted === true) throw callerAbortError(exchange);
    if (exchange.timedOut) {
      throw new TimeoutError(
        `${exchange.label} timed out after ${exchange.timeoutMs}ms`,
      );
    }
    // Fetch の仕様でいう network error。通信の失敗のほか、CSP や mode・redirect の設定ミスもここに来るため、まとめて NetworkError にする
    throw new NetworkError(failureMessage, { cause: error });
  }
}

// abort(reason) に任意の値が渡されても、呼び出し側の中断は AbortError として返し、キャンセルとして扱えるようにする。
// ただし AbortSignal.timeout() による中断はキャンセルではないので、黙って捨てられないよう TimeoutError にする
function callerAbortError(exchange: Exchange): unknown {
  const reason: unknown = exchange.signal?.reason;
  if (reason instanceof DOMException && reason.name === 'TimeoutError') {
    return new TimeoutError(
      `${exchange.label} timed out by the caller's signal`,
    );
  }
  return reason instanceof DOMException && reason.name === 'AbortError'
    ? reason
    : new DOMException('The operation was aborted.', 'AbortError');
}
